import React, { useState } from "react";
import { AlertTriangle, CreditCard, Loader2 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { billingMessage, billingState, BILLING_RESTRICTED } from "../constants/billing";
import { assertNotStaging } from "../../config/environment";

/**
 * The thing a coach with a failed card actually sees.
 *
 * One banner, one button, and the button does the only thing that helps:
 * opens the Stripe Customer Portal on their card. No "contact support", no
 * "visit settings then find subscription then", because every step between
 * the message and the fix is a place to give up, and 25% of lost members
 * are people whose card failed rather than people who chose to leave.
 *
 * Not dismissible while restricted. Dismissible during grace, per session
 * only, so it comes back on the next load: a coach who has seen it and is
 * mid-practice-plan should not have to read it on every page, but they
 * should not be able to make it go away for good either.
 */
export default function PastDueBanner({ profile, C, dark }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [hidden, setHidden] = useState(false);

  const msg = billingMessage(profile);
  const restricted = billingState(profile) === BILLING_RESTRICTED;
  if (!msg || (hidden && !restricted)) return null;

  const openPortal = async () => {
    setBusy(true); setErr(null);
    try {
      assertNotStaging("The billing portal");
      const { data: { session } } = await supabase.auth.getSession();
      const base = process.env.REACT_APP_SUPABASE_URL;
      const res = await fetch(`${base}/functions/v1/create-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      const j = await res.json();
      if (j?.url) { window.location.href = j.url; return; }
      throw new Error(j?.error || "no portal url");
    } catch (e) {
      // Never leave them staring at a dead button. The email address is
      // the fallback path, and it is in the message rather than a console.
      setErr("Could not open the billing page. Email support@byanymeanscoaches.com and we'll fix it for you.");
      setBusy(false);
    }
  };

  const accent = restricted ? "#F0B5A1" : GOLD;
  return (
    <div role="alert" style={{
      display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
      padding: "12px 16px",
      background: dark ? `${accent}14` : `${accent}20`,
      borderBottom: `1px solid ${accent}55`,
    }}>
      <AlertTriangle size={17} color={accent} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginBottom: 2 }}>{msg.title}</div>
        <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.5 }}>{msg.body}</div>
        {err && <div style={{ fontSize: 12, color: "#F0B5A1", marginTop: 6 }}>{err}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {!restricted && (
          <button onClick={() => setHidden(true)}
            style={{ padding: "8px 10px", fontSize: 12, fontWeight: 600, background: "transparent",
              border: "none", color: C.textDim, cursor: "pointer" }}>
            Later
          </button>
        )}
        <button onClick={openPortal} disabled={busy}
          style={{ display: "inline-flex", alignItems: "center", gap: 6,
            padding: "9px 15px", borderRadius: 9, fontSize: 12.5, fontWeight: 800,
            background: accent, color: "#111", border: "none",
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}>
          {busy ? <Loader2 size={13} className="bam-spin" /> : <CreditCard size={13} />}
          {msg.cta}
        </button>
      </div>
    </div>
  );
}
